import React, { useEffect, useMemo, useState } from 'react';
import { FlowProvider } from '../flow/FlowProvider';
import { normalizeFlowDefinitions, normalizePageFlowState } from '../../lib/flows';
import { supabase } from '../../lib/supabase';
import { validateAndNormalizeHomepageLayout } from '../../lib/layoutValidation';
import { resolveStagePageSections } from '../../lib/stageLayoutRules';
import { useDesignSystemSettings } from '../../hooks/useDesignSystemSettings';
import {
  getHomepageSectionWrapperClasses,
  HOMEPAGE_SINGLETON_SECTION_TYPES,
  HOMEPAGE_SECTION_REGISTRY,
  HomepageLayoutContent,
  HomepageLayoutSection,
  HomepageSectionRenderContext,
  normalizeHomepageSectionSettings,
} from './sectionRegistry';
import { resolveSectionAnchorId } from '../../lib/sectionAnchors';

interface PageLayoutsPublicRow {
  page_key: string;
  published_content: HomepageLayoutContent | null;
}

function hasRenderableSections(content: HomepageLayoutContent | null | undefined) {
  return Array.isArray(content?.sections) && content.sections.length > 0;
}

const EMPTY_LAYOUT_CONTENT: HomepageLayoutContent = {
  version: 1,
  sections: [],
  flows: [],
  flowState: {},
};

function normalizeHomepageLayoutContent(
  content: HomepageLayoutContent | null | undefined,
  fallback: HomepageLayoutContent = EMPTY_LAYOUT_CONTENT
): HomepageLayoutContent {
  const flows = normalizeFlowDefinitions(content?.flows);
  const flowState = normalizePageFlowState(content?.flowState, flows);
  const rawSections = Array.isArray(content?.sections) ? content!.sections : fallback.sections;
  const normalized: HomepageLayoutSection[] = [];
  const seenSingletons = new Set<HomepageSectionType>();

  for (const raw of rawSections) {
    if (!raw || typeof raw !== 'object') continue;
    const type = (raw as any).type as HomepageSectionType;
    if (!type || !(type in HOMEPAGE_SECTION_REGISTRY)) continue;

    const enabled = (raw as any).enabled;
    if (enabled === false) continue;

    if (HOMEPAGE_SINGLETON_SECTION_TYPES.has(type)) {
      if (seenSingletons.has(type)) continue;
      seenSingletons.add(type);
    }

    normalized.push({
      type,
      id: typeof (raw as any).id === 'string' ? (raw as any).id : undefined,
      enabled: true,
      settings: normalizeHomepageSectionSettings((raw as any).settings, {
        sectionType: type,
      }),
    });
  }

  if (normalized.length === 0) {
    return {
      ...fallback,
      flows,
      flowState,
    };
  }

  return {
    version: typeof content?.version === 'number' ? content.version : fallback.version,
    flows,
    flowState,
    sections: resolveStagePageSections(normalized),
  };
}

function layoutsEqual(
  a: HomepageLayoutContent | null | undefined,
  b: HomepageLayoutContent | null | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (JSON.stringify(a.flows ?? []) !== JSON.stringify(b.flows ?? [])) return false;
  if (a.sections.length !== b.sections.length) return false;
  for (let i = 0; i < a.sections.length; i += 1) {
    const left = a.sections[i];
    const right = b.sections[i];
    if (left.type !== right.type) return false;
    if ((left.id ?? '') !== (right.id ?? '')) return false;
    if (JSON.stringify(left.settings ?? null) !== JSON.stringify(right.settings ?? null)) return false;
  }
  return true;
}

function isStepTrackerOnlySection(section: HomepageLayoutSection): boolean {
  const blocks = section.settings?.canvas?.blocks;
  return section.type === 'canvas' && Array.isArray(blocks) && blocks.length > 0 && blocks.every((block) => block.type === 'site-progress');
}

type HomepageLayoutRendererBodyProps = {
  layout: HomepageLayoutContent;
  designSystemSettings: HomepageSectionRenderContext['designSystemSettings'];
  renderContext: HomepageSectionRenderContext;
};

const HomepageLayoutRendererBody: React.FC<HomepageLayoutRendererBodyProps> = ({
  layout,
  designSystemSettings,
  renderContext,
}) => {
  return (
    <>
      {layout.sections.map((section, index) => {
        const renderSection = HOMEPAGE_SECTION_REGISTRY[section.type];
        if (!renderSection) return null;
        const key = section.id ?? (HOMEPAGE_SINGLETON_SECTION_TYPES.has(section.type) ? section.type : `${section.type}-${index}`);
        const collapseStepTrackerSection =
          isStepTrackerOnlySection(section) && !renderContext.canvasAdminPreview?.enabled;
        if (collapseStepTrackerSection) {
          return (
            <div
              key={key}
              id={resolveSectionAnchorId(section.id)}
              data-section-id={section.id || undefined}
              data-step={section.settings?.progressStepId || undefined}
              className="contents"
            >
              {renderSection(renderContext, section)}
            </div>
          );
        }
        const wrapper = getHomepageSectionWrapperClasses(section.settings, designSystemSettings);
        return (
          <div
            key={key}
            id={resolveSectionAnchorId(section.id)}
            data-section-id={section.id || undefined}
            data-step={section.settings?.progressStepId || undefined}
            className={`${wrapper.outer} relative`}
            style={wrapper.outerStyle}
          >
            <div className={`${wrapper.inner} relative`}>{renderSection(renderContext, section)}</div>
          </div>
        );
      })}
    </>
  );
};

type HomepageLayoutRendererProps = HomepageSectionRenderContext & {
  pageKey?: string;
  fallbackLayout?: HomepageLayoutContent;
  transformLayout?: (layout: HomepageLayoutContent) => HomepageLayoutContent;
  onLayoutResolved?: (layout: HomepageLayoutContent) => void;
  deferInitialRender?: boolean;
  loadingFallback?: React.ReactNode;
};

const HomepageLayoutRenderer: React.FC<HomepageLayoutRendererProps> = ({
  pageKey = 'home',
  fallbackLayout,
  transformLayout,
  onLayoutResolved,
  deferInitialRender = false,
  loadingFallback = null,
  ...props
}) => {
  const safeFallbackLayout = fallbackLayout ?? EMPTY_LAYOUT_CONTENT;
  const defaultLayout = useMemo(
    () => normalizeHomepageLayoutContent(safeFallbackLayout, safeFallbackLayout),
    [safeFallbackLayout]
  );
  const [layout, setLayout] = useState<HomepageLayoutContent | null>(() =>
    deferInitialRender ? null : defaultLayout
  );
  const { settings: designSystemSettings } = useDesignSystemSettings('published');
  const renderContext = useMemo<HomepageSectionRenderContext>(
    () => ({
      ...props,
      designSystemSettings,
    }),
    [props, designSystemSettings]
  );
  const effectiveLayout = useMemo(
    () => (layout ? (transformLayout ? transformLayout(layout) : layout) : null),
    [layout, transformLayout]
  );

  useEffect(() => {
    if (effectiveLayout) {
      onLayoutResolved?.(effectiveLayout);
    }
  }, [effectiveLayout, onLayoutResolved]);

  useEffect(() => {
    let cancelled = false;

    const loadPublishedLayout = async () => {
      try {
        const { data, error } = await supabase
          .from('page_layouts_public')
          .select('page_key, published_content')
          .eq('page_key', pageKey)
          .maybeSingle<PageLayoutsPublicRow>();

        if (cancelled) return;
        if (error) {
          if (import.meta.env.DEV) console.warn('[HomepageLayoutRenderer] Using fallback layout', error);
          setLayout((prev) => (layoutsEqual(prev, defaultLayout) ? prev : defaultLayout));
          return;
        }

        const validated = validateAndNormalizeHomepageLayout(
          data?.published_content ?? null,
          {
            mode: 'load',
            pageKey,
            includeDisabled: false,
            fallback: safeFallbackLayout,
          }
        );
        const normalized =
          !hasRenderableSections(data?.published_content) && !fallbackLayout
            ? defaultLayout
            : normalizeHomepageLayoutContent(validated.layout, safeFallbackLayout);
        setLayout((prev) => (layoutsEqual(prev, normalized) ? prev : normalized));
      } catch (error) {
        if (!cancelled && import.meta.env.DEV) {
          console.warn(
            `[HomepageLayoutRenderer] Failed to load page layout for ${pageKey}, using fallback`,
            error
          );
        }
        if (!cancelled) {
          setLayout((prev) => (layoutsEqual(prev, defaultLayout) ? prev : defaultLayout));
        }
      }
    };

    void loadPublishedLayout();

    return () => {
      cancelled = true;
    };
  }, [defaultLayout, fallbackLayout, pageKey, safeFallbackLayout]);

  if (!effectiveLayout) {
    return <>{loadingFallback}</>;
  }

  return (
    <FlowProvider layout={effectiveLayout}>
      <HomepageLayoutRendererBody
        layout={effectiveLayout}
        designSystemSettings={designSystemSettings}
        renderContext={renderContext}
      />
    </FlowProvider>
  );
};

export default HomepageLayoutRenderer;
